/**
 * socials.ts — reti sociali del club.
 *
 * I link vivono in `club_sites.social_links` come mappa `rete → URL` (chiave
 * minuscola: `facebook`, `instagram`, …). Questo modulo è l'**unica fonte**
 * dell'elenco delle reti supportate, dell'ordine di visualizzazione e della
 * normalizzazione, ed è senza dipendenze server: lo importano il form
 * dell'area riservata e la vetrina «Club accreditati».
 */

/** Massimo numero di social mostrabili sul badge del club. */
export const MAX_CLUB_SOCIALS = 4

export interface SocialNetwork {
  id: string
  label: string
}

/** Reti supportate, nell'ordine in cui compaiono sul badge. */
export const SOCIAL_NETWORKS: SocialNetwork[] = [
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'x', label: 'X (Twitter)' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'telegram', label: 'Telegram' },
]

/** Alias accettati in lettura (dati storici o scritti a mano nel vecchio campo JSON). */
const ALIASES: Record<string, string> = {
  twitter: 'x',
  'x-twitter': 'x',
  fb: 'facebook',
  ig: 'instagram',
  yt: 'youtube',
}

export function normalizeSocialNetwork(raw: string): string {
  const key = raw.trim().toLowerCase()
  return ALIASES[key] ?? key
}

export function isSupportedNetwork(id: string): boolean {
  return SOCIAL_NETWORKS.some((n) => n.id === id)
}

export function socialNetworkLabel(id: string): string {
  return SOCIAL_NETWORKS.find((n) => n.id === id)?.label ?? id
}

/** Un social del club, pronto per il rendering (rete, etichetta, URL). */
export interface ClubSocial {
  network: string
  label: string
  url: string
}

function cleanUrl(raw: unknown): string {
  return typeof raw === 'string' ? raw.trim() : ''
}

/**
 * Social del club dalla mappa salvata: solo reti supportate, senza URL vuoti,
 * senza duplicati, nell'ordine di `SOCIAL_NETWORKS` e **al massimo 4**.
 */
export function clubSocials(links: Record<string, unknown> | null | undefined): ClubSocial[] {
  if (!links) return []
  const byId = new Map<string, string>()
  for (const [rawKey, rawValue] of Object.entries(links)) {
    const id = normalizeSocialNetwork(rawKey)
    const url = cleanUrl(rawValue)
    if (!url || !isSupportedNetwork(id) || byId.has(id)) continue
    byId.set(id, url)
  }
  return SOCIAL_NETWORKS
    .filter((n) => byId.has(n.id))
    .slice(0, MAX_CLUB_SOCIALS)
    .map((n) => ({ network: n.id, label: n.label, url: byId.get(n.id)! }))
}

/**
 * Mappa `rete → URL` da salvare in `club_sites.social_links`: scarta reti non
 * supportate, URL vuoti e duplicati, e si ferma a 4. Usata sia dal form sia
 * dalla route, così il limite non è solo un vincolo di interfaccia.
 */
export function socialsToLinks(entries: { network: string; url: string }[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const entry of entries) {
    const id = normalizeSocialNetwork(entry.network)
    const url = cleanUrl(entry.url)
    if (!url || !isSupportedNetwork(id) || out[id]) continue
    out[id] = url
    if (Object.keys(out).length >= MAX_CLUB_SOCIALS) break
  }
  return out
}

/** `true` se l'URL può essere usato come link social (http/https). */
export function isValidSocialUrl(url: string): boolean {
  return /^https?:\/\/\S+$/i.test(url.trim())
}
